import { IconButton, Tooltip } from '@material-ui/core';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BreadCrumb from '../../../component/BreadCrumb';
import { CODE_GROUP_ID } from '../../../constant/Constant';
import CodeUtil from '../../../util/CodeUtil';
import PopupRef from '../../../component/PopupRef';
import SystemSubjectCodeModal from '../../modal/SystemSubjectCodeModal';
import { modalType } from '../../../util/ModalUtil';
import ModalRef from '../../../component/ModalRef';
import SelectionBox from '../../../component/SelectionBox';
import InputBox from '../../../component/InputBox';
import RegistButtonBox from '../../../component/buttons/RegistButtonBox';
import DeleteIcon from '../../../component/icon/DeleteIcon';
import { actionSearch, actionRemove } from '../../../module/body/system/SystemSubjectCodeModule';
import { VIEW_SYSTEM_SUBJECT_CODE } from './../../../constant/ViewConstant';
import SearchButtonBox from '../../../component/buttons/SearchButtonBox';
import { defaultGridData, handleControl, initGrid } from '../../../module/component/TableBoxModule';
import TableBox from '../../../component/TableBox';
import { OpenEditIcon } from '../../../component/icon/GridIcons';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import NullCheckUtil from '../../../util/NullCheckUtil';
import PathUtil from '../../../util/PathUtil';

const ConditionArea = ({ popupRef, modalRef }) => {
    const location = useLocation();
    const queryData = qs.parse(location.search, { ignoreQueryPrefix: true });
    // condition
    const [subjectTypeCd, setSubjectTypeCd] = useState('');
    const [subjectNm, setSubjectNm] = useState('');
    const [subjectDesc, setSubjectDesc] = useState('');
    const [params, setParams] = useState({});

    const dispatch = useDispatch();
    const { gridData } = useSelector((state) => state.tableBoxState);

    // 최초 초기화 호출
    useEffect(() => {
        dispatch(
            handleControl({
                columns,
                onRowDoubleClick,
                onChangePageInfo,
                params,
            }),
        );

        queryData.subjectTypeCd && setSubjectTypeCd(queryData.subjectTypeCd);
        queryData.subjectNm && setSubjectNm(queryData.subjectNm);
        queryData.subjectDesc && setSubjectDesc(queryData.subjectDesc);

        onClickSearch({
            subjectTypeCd: queryData.subjectTypeCd,
            subjectNm: queryData.subjectNm,
            subjectDesc: queryData.subjectDesc,
            page: NullCheckUtil.isNotNull(queryData.page) ? queryData.page : defaultGridData.page,
            pageSize: NullCheckUtil.isNotNull(queryData.pageSize) ? queryData.pageSize : 100,
        });

        return () => {
            dispatch(initGrid());
        };
    }, []);

    useEffect(() => {
        dispatch(
            handleControl({
                columns,
                onRowDoubleClick,
                onChangePageInfo,
                params,
            }),
        );
    }, [params]);

    const onChangePageInfo = (page, pageSize) => {
        onClickSearch({
            ...params,
            page,
            pageSize,
        });
    };

    const onClickSearch = (params) => {
        setParams(params);
        dispatch(
            actionSearch({
                params,
                callback: (result) => {
                    if (result.totalCount == 0) {
                        popupRef.current.openAlert('조회된 결과가 없습니다.');
                    }
                },
            }),
        );
        PathUtil.pushState(location.pathname, params);
    };

    const onRowDoubleClick = (row, params) => {
        modalRef.current.open(
            <SystemSubjectCodeModal
                type={modalType.EDIT}
                row={row}
                callback={(result) => {
                    modalRef.current.close();
                    popupRef.current.openInfo('과목 ' + result.subjectNm + '(가)이 수정되었습니다.', () => {
                        return function () {
                            onClickSearch(params);
                        };
                    });
                }}
                modalRef={modalRef}
            />,
        );
    };

    const handleDelete = (row, params) => {
        popupRef.current.openWarning('교과목 ' + row.subjectNm + '을(를) 삭제하시겠습니까?', () => {
            return function () {
                dispatch(
                    actionRemove({
                        data: {
                            subjectId: row.subjectId,
                            subjectNm: row.subjectNm,
                        },
                        callback: (result) => {
                            popupRef.current.openInfo('과목 ' + result.subjectNm + '(가)이 삭제되었습니다.', () => {
                                return function () {
                                    onClickSearch(params);
                                };
                            });
                        },
                    }),
                );
            };
        });
    };

    const handleAdd = (params) => {
        modalRef.current.open(
            <SystemSubjectCodeModal
                type={modalType.ADD}
                callback={(result) => {
                    modalRef.current.close();
                    popupRef.current.openInfo('과목 ' + result.subjectNm + '(가)이 등록되었습니다.', () => {
                        return function () {
                            onClickSearch(params);
                        };
                    });
                }}
                modalRef={modalRef}
            />,
        );
    };

    const onKeyDown = (e) => {
        if (e.key == 'Enter') {
            onClickSearch({
                subjectTypeCd,
                subjectNm,
                page: defaultGridData.page,
                pageSize: gridData.pageSize,
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                field: 'subjectTypeCd',
                headerName: '과정구분',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 15,
                renderCell: (params) => CodeUtil.getCodeName(CODE_GROUP_ID.SUBJECT_TYPE, params.row.subjectTypeCd),
            },
            {
                field: 'subjectNm',
                headerName: '교과목',
                headerAlign: 'center',
                align: 'left',
                tooltip: true,
                flex: 30,
            },
            {
                field: 'subjectDesc',
                headerName: '비고',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
            },

            {
                field: 'detail',
                headerName: '기능',
                headerAlign: 'center',
                align: 'center',
                flex: 5,
                disableColumnMenu: true,
                hideSortIcons: true,
                disableExport: true,
                renderCell: (item) => (
                    <>
                        <Tooltip title='수정'>
                            <IconButton onClick={() => onRowDoubleClick(item.row, params)}>
                                <OpenEditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='삭제'>
                            <IconButton onClick={(e) => handleDelete(item.row, params)}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                ),
            },
        ],
        [params],
    );

    return (
        <>
            <div className='con-form-wrap '>
                <div className='board-inner'>
                    <div className='board-row'>
                        <div className='lec-check-box'>
                            <div className='search-condition-box'>
                                <div className='form-box-container'>
                                    <SelectionBox
                                        title='과정구분'
                                        w140
                                        options={CodeUtil.getCodes(CODE_GROUP_ID.SUBJECT_TYPE)}
                                        value={subjectTypeCd}
                                        onChange={(e) => setSubjectTypeCd(e.target.value)}
                                        onKeyDown={(e) => onKeyDown(e)}
                                    />
                                    <InputBox
                                        title='교과목'
                                        value={subjectNm}
                                        onChange={(e) => setSubjectNm(e.target.value)}
                                        onKeyDown={(e) => onKeyDown(e)}
                                    />
                                    <InputBox
                                        title='비고'
                                        value={subjectDesc}
                                        onChange={(e) => setSubjectDesc(e.target.value)}
                                        onKeyDown={(e) => onKeyDown(e)}
                                    />
                                </div>
                            </div>
                            <div className='lec-check-btn'>
                                <SearchButtonBox
                                    title='조회'
                                    onClick={(e) =>
                                        onClickSearch({
                                            subjectTypeCd,
                                            subjectNm,
                                            subjectDesc,
                                            page: defaultGridData.page,
                                            pageSize: gridData.pageSize,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='con-tb-wrap'>
                <div className='con-tb-title  '>
                    <h3 className='tb-tit-name'>과목코드 목록</h3>
                    <RegistButtonBox title='등록' onClick={(e) => handleAdd(params)} />
                </div>
            </div>
        </>
    );
};

const SystemSubjectCodeView = () => {
    const popupRef = useRef(null);
    const modalRef = useRef(null);

    return (
        <section className='container'>
            <div className='container-inner'>
                <div className='con-title-wrap conbg02'>
                    {/* <h2 className='con-title'>과목 코드 관리</h2> */}
                    <h2 className='con-title'>{VIEW_SYSTEM_SUBJECT_CODE.title}</h2>
                    <p className='con-title-p'>{VIEW_SYSTEM_SUBJECT_CODE.subTitle}</p>
                </div>
                <div className='sub-nav-wrap'>
                    <BreadCrumb />
                </div>
                <ConditionArea popupRef={popupRef} modalRef={modalRef} />
                <TableBox isSupportedWide={false} />
            </div>
            <PopupRef ref={popupRef} />
            <ModalRef ref={modalRef} />
        </section>
    );
};

export default SystemSubjectCodeView;
